import { computed } from 'vue'
import i18n, { isTranslationHistoryTrackEnabled } from '@/i18n'
import { ColumnLabel, WidgetTemplateColumnWithProp } from '@/types/dashboard'
import { DefaultAccountBreak, AccountBreak } from '@/types/entities'
import { RealtimeDynamicColumn } from '@/enum/realtimeTableConfig'
import { entitiesData, selectedAccountData } from '@/composables/globalData/useEntitiesData'

interface SimpleColumn {
    prop: string
}

const LOGIN_STATUS_NAMES_COLUMNS = [ 'break', 'duration' ]
export default function useTableHeaderTranslations () {
    const defaultAccountBreakList = computed(() => entitiesData.value.DefaultAccountBreak)
    const currentAccountBreakList = computed(() => selectedAccountData.value?.AccountBreaks)

    const getColumnTagsByProp = (columnProp: string) => {
        const translateOfHeaderTitleInitials = `widget.table.header.title.initials.${columnProp}`
        const translateOfHeaderTitleFull = `widget.table.header.title.full.${columnProp}`
        const translateOfHeaderTitleIcon = `widget.table.header.title.icon.${columnProp}`

        return {
            translateOfHeaderTitleInitials,
            translateOfHeaderTitleFull,
            translateOfHeaderTitleIcon
        }
    }

    const getColumnTags = (column: WidgetTemplateColumnWithProp | RealtimeDynamicColumn | SimpleColumn) => getColumnTagsByProp(column.prop)

    const makeTableHeaderTranslations = (column: WidgetTemplateColumnWithProp | RealtimeDynamicColumn | SimpleColumn, baseLabel:string): ColumnLabel => {
        const { translateOfHeaderTitleInitials, translateOfHeaderTitleFull, translateOfHeaderTitleIcon } = getColumnTags(column)
        return getTableHeadersTranslations(translateOfHeaderTitleInitials, translateOfHeaderTitleFull, translateOfHeaderTitleIcon, baseLabel)
    }

    const makeTableHeaderTranslationsByProp = (prop: string) => {
        const { translateOfHeaderTitleInitials, translateOfHeaderTitleFull, translateOfHeaderTitleIcon } = getColumnTagsByProp(prop)
        return getTableHeadersTranslations(translateOfHeaderTitleInitials, translateOfHeaderTitleFull, translateOfHeaderTitleIcon, prop)
    }

    const getTooltipTranslation = (translateOfHeaderTitleFull: string, translateOfHeaderTitleInitials: string, translateOfHeaderTitleIcon: string, baseLabel:string) => {
        const translation = i18n.global.te(translateOfHeaderTitleFull)
            ? i18n.global.t(translateOfHeaderTitleFull) as string
            : baseLabel

        const shouldShowTranslationTags = process.env.NODE_ENV === 'development' || isTranslationHistoryTrackEnabled()

        if (shouldShowTranslationTags) {
            return `${translation}\n('${translateOfHeaderTitleFull}'/'${translateOfHeaderTitleInitials}/${translateOfHeaderTitleIcon}')`
        } else {
            return replaceDefaultLoginStatusNameWithAccountBreakName(translation)
        }
    }

    const makeTableHeaderTooltipTranslations = (column: WidgetTemplateColumnWithProp | RealtimeDynamicColumn | SimpleColumn, baseLabel: string) => {
        const { translateOfHeaderTitleFull, translateOfHeaderTitleInitials, translateOfHeaderTitleIcon } = getColumnTags(column)

        return getTooltipTranslation(translateOfHeaderTitleFull, translateOfHeaderTitleInitials, translateOfHeaderTitleIcon, baseLabel)
    }

    const makeTableHeaderTooltipTranslationsByProp = (prop: string) => {
        const { translateOfHeaderTitleFull, translateOfHeaderTitleInitials, translateOfHeaderTitleIcon } = getColumnTagsByProp(prop)
        return getTooltipTranslation(translateOfHeaderTitleFull, translateOfHeaderTitleInitials, translateOfHeaderTitleIcon, prop)
    }

    const normalizeString = (str: string): string => {
        return str
            .trim()
            .replace(/\s+/g, ' ')
    }

    const replaceBreakNameInTranslation = (
        translation: string,
        defaultBreak: DefaultAccountBreak,
        customBreak: AccountBreak
    ): string => {
        const normalizedTranslation = normalizeString(translation)
        const normalizedCustomName = normalizeString(customBreak.Name)

        const statusKeyLower = normalizeString(defaultBreak.Name)
            .toLowerCase()
            .replace(/\s+/g, '_')

        const statusKeys = [
            `status.break.name.${statusKeyLower}`,
            `status.break.name.${statusKeyLower}.alt1`,
        ]

        const words = normalizedTranslation.split(/\s+/)
        let foundIndex = -1
        let foundWordsCount = 1
        let bestMatchLength = 0

        for (const statusKey of statusKeys) {
            if (!i18n.global.te(statusKey)) continue

            const statusTranslation = normalizeString(i18n.global.t(statusKey) as string)
            const statusWords = statusTranslation.split(/\s+/)

            for (let i = 0; i < words.length; i++) {

                let matchCount = 0
                let totalMatchLength = 0

                for (let j = 0; j < statusWords.length && i + j < words.length; j++) {
                    const statusWord = statusWords[j]
                    const word = words[i + j]

                    if (word === statusWord) {
                        matchCount++
                        totalMatchLength += statusWord.length
                        continue
                    }

                    if (statusWord.length >= 3 && word.length >= 3) {
                        const minLength = Math.min(statusWord.length, word.length)
                        const rootLength = Math.min(4, minLength)
                        const statusRoot = statusWord.substring(0, rootLength)
                        const wordRoot = word.substring(0, rootLength)

                        if (statusRoot === wordRoot) {
                            matchCount++
                            totalMatchLength += rootLength
                            continue
                        }
                    }

                    break
                }

                if (matchCount > 0 && totalMatchLength > bestMatchLength) {
                    foundIndex = i
                    foundWordsCount = matchCount
                    bestMatchLength = totalMatchLength
                }
            }

            if (foundIndex !== -1 && foundWordsCount === statusWords.length) {
                break
            }
        }

        if (foundIndex !== -1) {
            const locale = i18n.global.locale.value

            let customNameWithMarkers = normalizedCustomName
            if (locale === 'he' && /[a-zA-Z0-9А-Яа-я]/.test(normalizedCustomName)) {
                customNameWithMarkers = `\u200E${normalizedCustomName}\u200E`
            }

            words.splice(foundIndex, foundWordsCount, customNameWithMarkers)

            const result = words.join(' ')
            return locale === 'he' ? `\u200F${result}` : result
        }

        return normalizedTranslation
    }

    const findDefaultBreakByProp = (prop: string) => {
        if (!prop || !isLoginStatusColumnByLoginStatusNamesColumns(prop)) {
            return null
        }

        const normalizedProp = prop.replace(/\s+/g, '').toLowerCase()

        return defaultAccountBreakList.value.find((el) => {
            const normalizedName = normalizeString(el.Name).replace(/\s+/g, '').toLowerCase()
            return normalizedProp.includes(normalizedName)
        })
    }

    const getTableHeadersTranslations = (
        translateOfHeaderTitleInitials: string,
        translateOfHeaderTitleFull: string,
        translateOfHeaderTitleIcon: string,
        baseLabel: string
    ) => {
        const prop = translateOfHeaderTitleFull.split('.').pop() || ''

        const defaultBreak = findDefaultBreakByProp(prop)

        let translationFull = baseLabel

        if (defaultBreak) {
            const customBreak = getCurrentAccountBreakById(defaultBreak.StatusID)
            if (customBreak) {

                if (i18n.global.te(translateOfHeaderTitleFull)) {
                    translationFull = i18n.global.t(translateOfHeaderTitleFull) as string
                }

                translationFull = replaceBreakNameInTranslation(
                    translationFull,
                    defaultBreak,
                    customBreak
                )
            }
        } else {
            if (i18n.global.te(translateOfHeaderTitleFull)) {
                translationFull = i18n.global.t(translateOfHeaderTitleFull) as string
            }
            translationFull = replaceDefaultLoginStatusNameWithAccountBreakName(translationFull)
        }

        let translationShort = ''

        if (i18n.global.te(translateOfHeaderTitleInitials)) {
            translationShort = i18n.global.t(translateOfHeaderTitleInitials) as string
            if (defaultBreak) {
                const customBreak = getCurrentAccountBreakById(defaultBreak.StatusID)
                if (customBreak) {
                    translationShort = replaceBreakNameInTranslation(
                        translationShort,
                        defaultBreak,
                        customBreak
                    )
                }
            } else {
                translationShort = replaceDefaultLoginStatusNameWithAccountBreakName(translationShort)
            }
        } else {
            translationShort = translationFull.split(' ').reduce((acc, word) => {
                acc += word[0]
                return acc
            }, '')
        }

        const translationIcon = i18n.global.te(translateOfHeaderTitleIcon)
            ? i18n.global.t(translateOfHeaderTitleIcon) as string
            : ''

        return {
            shortTextLabel: translationShort,
            fullTextLabel: translationFull,
            iconLabel: translationIcon
        }
    }
    const getDefaultAccountBreak = (columnName: string) => {
        const columnNameTrimmed = columnName.trim()
        return defaultAccountBreakList.value.find((el) => {
            const regex = new RegExp(el.Name.trim(), 'i')
            return regex.test(columnNameTrimmed)
        })
    }

    const getCurrentAccountBreakById = (id: string| number) => {
        return currentAccountBreakList.value?.find((el) => el.StatusID.toString() === id.toString())
    }

    const isLoginStatusColumnByLoginStatusNamesColumns = (columnName: string) => {
        return LOGIN_STATUS_NAMES_COLUMNS.find((el) => columnName.trim().toLowerCase().includes(el)) !== undefined
    }

    const replaceDefaultLoginStatusNameWithAccountBreakName = (columnName: string) => {

        const defaultAccountBreak = getDefaultAccountBreak(columnName)

        if (!defaultAccountBreak || !isLoginStatusColumnByLoginStatusNamesColumns(columnName)) {
            return columnName
        }

        const currentAccountBreak = getCurrentAccountBreakById(defaultAccountBreak.StatusID)

        if (!currentAccountBreak) {
            return columnName
        }

        const regex = new RegExp(defaultAccountBreak.Name.trim(), 'i')
        return columnName.replace(regex, currentAccountBreak.Name.trim())
    }

    return {
        getColumnTagsByProp,
        makeTableHeaderTranslations,
        makeTableHeaderTranslationsByProp,
        makeTableHeaderTooltipTranslations,
        makeTableHeaderTooltipTranslationsByProp
    }
}
